import * as React from "react"

import { cn } from "@/lib/utils"

import type { FindingAxes } from "../data/review-data"

/**
 * The four-axis spine. Never merges (spec §1: confirmed-and-corrected is a
 * different animal than confirmed-and-open). Reading top → bottom:
 *   verdict — is it real?
 *   state   — is it fixed?
 *   client  — did the client agree?
 *   origin  — where did it come from?
 *
 * Under a screen reader the axes render as a single text summary rather
 * than four cryptic bars — pass `summary` when the record has room.
 */

const VERDICT_TONE: Record<FindingAxes["verdict"], string> = {
  unset: "bg-border-strong",
  confirmed: "bg-master",
  rejected: "bg-text-3",
  needs_sme: "bg-people",
}

const STATE_TONE: Record<FindingAxes["state"], string> = {
  open: "bg-border-strong",
  candidate_corrected: "bg-print",
  corrected_confirmed: "bg-master",
  resolved: "bg-master-strong",
}

const CLIENT_TONE: Record<FindingAxes["clientResolution"], string> = {
  none: "bg-border-strong",
  acknowledged: "bg-info",
  disputed: "bg-err",
  done: "bg-master",
}

const ORIGIN_TONE: Record<FindingAxes["origin"], string> = {
  new: "bg-info",
  carried_over: "bg-print",
  regression: "bg-err",
  manual: "bg-brand",
}

export interface FindingSpineProps {
  axes: FindingAxes
  summary?: string
  className?: string
}

export function FindingSpine({ axes, summary, className }: FindingSpineProps): JSX.Element {
  return (
    <span
      aria-hidden={summary ? "true" : undefined}
      className={cn("inline-flex flex-col items-center gap-[2px]", className)}
    >
      <span className={cn("block h-[3px] w-4 rounded-full", VERDICT_TONE[axes.verdict])} />
      <span className={cn("block h-[3px] w-4 rounded-full", STATE_TONE[axes.state])} />
      <span className={cn("block h-[3px] w-4 rounded-full", CLIENT_TONE[axes.clientResolution])} />
      <span className={cn("block h-[3px] w-4 rounded-full", ORIGIN_TONE[axes.origin])} />
      {summary && <span className="sr-only">{summary}</span>}
    </span>
  )
}
